import { useState } from "react";
import { BookOpen, Coins, Loader2, RefreshCw } from "lucide-react";
import { useUpdateCategoryRules } from "@/api/hooks";
import type { PatronCategory } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CATEGORY_RULE_SPECS, readDraft, toDraft } from "../../loanRules";
import { RuleGroup, UnitField } from "./fields";

type Field = keyof typeof CATEGORY_RULE_SPECS;

/**
 * Default loan rules of a patron category. Keyed by category in the page,
 * so the draft always starts from the category being shown.
 */
export function CategoryDefaultsCard({ category }: { category: PatronCategory }) {
  const initial = toDraft<Field>(category, CATEGORY_RULE_SPECS);
  const [draft, setDraft] = useState(initial);
  const [errors, setErrors] = useState<Partial<Record<Field, string | undefined>>>({});
  const [feedback, setFeedback] = useState<{ tone: "success" | "error"; text: string } | null>(
    null
  );
  const updateRules = useUpdateCategoryRules();

  const dirty = (Object.keys(initial) as Field[]).some((key) => draft[key] !== initial[key]);

  const set = (field: Field) => (value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setFeedback(null);
  };

  const field = (name: Field) => ({
    value: draft[name],
    onChange: set(name),
    error: errors[name],
  });

  const save = async () => {
    const { values, errors: draftErrors } = readDraft(draft, CATEGORY_RULE_SPECS);
    setErrors(draftErrors);
    if (!values) return;

    try {
      await updateRules.mutateAsync({ id: category.id, input: values });
      setFeedback({ tone: "success", text: "Règles par défaut enregistrées" });
    } catch (err) {
      setFeedback({
        tone: "error",
        text: err instanceof Error ? err.message : "L'enregistrement a échoué",
      });
    }
  };

  return (
    <section
      aria-labelledby="category-defaults-title"
      className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <header className="mb-5">
        <h2
          id="category-defaults-title"
          className="text-lg font-semibold text-gray-900 dark:text-white"
        >
          Règles par défaut
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          S&apos;appliquent à tous les documents et tous les sites, sauf exception ci-dessous.
        </p>
      </header>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        noValidate
      >
        <div className="grid gap-4 xl:grid-cols-3">
          <RuleGroup
            icon={BookOpen}
            title="Prêt"
            description="Durée et quotas simultanés"
            columns="stacked"
          >
            <UnitField label="Durée du prêt" unit="jours" {...field("defaultLoanDays")} />
            <UnitField label="Documents empruntés" unit="max." {...field("maxLoans")} />
            <UnitField label="Réservations" unit="max." {...field("maxReservations")} />
          </RuleGroup>

          <RuleGroup
            icon={RefreshCw}
            title="Prolongation"
            description="Repart du jour de la prolongation, pour la durée du prêt."
            columns="stacked"
          >
            <UnitField
              label="Prolongations par prêt"
              unit="max."
              hint="0 pour interdire la prolongation"
              {...field("maxRenewals")}
            />
            <UnitField
              label="Prolongeable dès"
              unit="j avant"
              hint="avant l'échéance ; un retard reste prolongeable"
              {...field("renewalWindowDays")}
            />
          </RuleGroup>

          <RuleGroup
            icon={Coins}
            title="Retards"
            description="Amende appliquée au retour"
            columns="stacked"
          >
            <UnitField
              label="Amende par jour"
              unit="€"
              inputMode="decimal"
              {...field("finePerDayInCents")}
            />
            <UnitField
              label="Plafond par document"
              unit="€"
              inputMode="decimal"
              hint="0 = sans plafond"
              {...field("maxFineInCents")}
            />
          </RuleGroup>
        </div>

        {(dirty || feedback) && (
          <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
            {feedback && (
              <p
                role={feedback.tone === "error" ? "alert" : "status"}
                className={cn(
                  "mr-auto text-sm",
                  feedback.tone === "success"
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-red-700 dark:text-red-400"
                )}
              >
                {feedback.text}
              </p>
            )}
            {dirty && (
              <>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Modifications non enregistrées
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setDraft(initial);
                    setErrors({});
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={updateRules.isPending}>
                  {updateRules.isPending && <Loader2 className="animate-spin" aria-hidden />}
                  Enregistrer
                </Button>
              </>
            )}
          </div>
        )}
      </form>
    </section>
  );
}
